import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { apiFetch } from './api';
import { BILLING_LABELS_SHORT, isBillingType } from './lib/billingType';
import { formatHours } from './lib/week';
import { DashboardStatsSkeleton } from './components/Skeleton';
import { TrendChart } from './components/TrendChart';

type DashboardScope = 'all' | 'owned' | 'none';

interface StageRow {
  stageId: string | null;
  stageLabel: string;
  count: number;
  amountCents: number | null;
}

interface FlatCountRow {
  billingType: string;
  count: number;
  minutes: number;
}

interface DashboardSummary {
  scope: DashboardScope;
  showFinancials: boolean;
  stages: StageRow[];
  minutesThisPeriod: number;
  hourlyMinutesThisPeriod: number;
  flatCountsThisPeriod: FlatCountRow[];
  payPeriodStartsAt: string;
  payPeriodEndsAt: string;
}

interface ByDealRow {
  dealId: string;
  hubspotId: string | null;
  dealName: string;
  jobNumber: string | null;
  amountCents: number | null;
  minutes: number;
  billedCents: number | null;
  moneyLeftCents: number | null;
  rateMissing: boolean;
}

interface ByDealResponse {
  scope: DashboardScope;
  showFinancials: boolean;
  payPeriodId: string;
  startsAt: string;
  endsAt: string;
  state: string;
  anyRateMissing: boolean;
  rows: ByDealRow[];
}

// PR 0007 — Dashboard takes no props. Identity is server-resolved via
// the Cloudflare Access JWT.

const TOP_N = 10;

// Brand-blue spectrum + complementary accents. Aligned with the existing
// brand-blue/brand-charcoal/brand-steel palette so the pie doesn't feel like
// a recharts default theme.
const SLICE_COLORS = [
  '#0079D2', '#26ABE2', '#3B82F6', '#6366F1', '#8B5CF6',
  '#0EA5E9', '#14B8A6', '#22C55E', '#EAB308', '#F97316',
  '#94A3B8', // "Other"
];

const cadFmt = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
});
const dateFmt = new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' });

function dealDisplayName(row: ByDealRow): string {
  if (row.jobNumber) return `${row.jobNumber} · ${row.dealName}`;
  return row.dealName;
}

interface PieDatum {
  name: string;
  minutes: number;
  hours: number;
}

function bucketTopN(rows: ByDealRow[]): PieDatum[] {
  if (rows.length === 0) return [];
  const top = rows.slice(0, TOP_N).map((r) => ({
    name: dealDisplayName(r),
    minutes: r.minutes,
    hours: r.minutes / 60,
  }));
  const rest = rows.slice(TOP_N);
  if (rest.length === 0) return top;
  const otherMinutes = rest.reduce((acc, r) => acc + r.minutes, 0);
  return [...top, { name: `Other (${rest.length})`, minutes: otherMinutes, hours: otherMinutes / 60 }];
}


// PR 0007 — Dashboard takes no props. Scope is resolved server-side from
// the Cloudflare Access JWT (supervisor -> 'all', employee with HubSpot
// owner -> 'owned', neither -> 'none').
export function Dashboard(): JSX.Element {
  const summaryQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => apiFetch<DashboardSummary>(`/dashboard`),
    refetchInterval: 30_000,
  });
  const byDealQuery = useQuery({
    queryKey: ['dashboard', 'by-deal'],
    queryFn: () => apiFetch<ByDealResponse>(`/dashboard/by-deal`),
    refetchInterval: 30_000,
  });

  const pieData = useMemo<PieDatum[]>(
    () => bucketTopN(byDealQuery.data?.rows ?? []),
    [byDealQuery.data],
  );

  if (summaryQuery.isLoading || !summaryQuery.data) {
    return <DashboardStatsSkeleton />;
  }

  const summary = summaryQuery.data;
  const scopeLabel =
    summary.scope === 'all'
      ? 'All deals'
      : summary.scope === 'owned'
        ? 'Your deals'
        : 'No HubSpot ownership';

  return (
    <>
      {/* Pipeline stage tiles are supervisor-only (Tim, 2026-07 feedback):
          employees found the deal-count tags noise on their logging surface.
          scope 'owned' and 'none' both skip the section entirely — including
          the old "not mapped to a HubSpot owner" banner, which only existed
          to explain empty pipeline widgets that no longer render. */}
      {summary.scope === 'all' && (
        <section className="flex flex-col gap-3 py-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-charcoal">
              Pipeline · {scopeLabel}
            </h2>
            <span className="text-xs text-brand-slate">
              {dateFmt.format(new Date(summary.payPeriodStartsAt))}–
              {dateFmt.format(new Date(summary.payPeriodEndsAt))}
            </span>
          </div>

          {summary.stages.length === 0 ? (
            <p className="rounded-xl bg-brand-steel p-4 text-sm text-brand-slate ring-1 ring-brand-steel">
              No deals to show.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {summary.stages.map((stage, idx) => (
                <StageTile
                  key={`${stage.stageId ?? 'unknown'}:${stage.stageLabel}`}
                  label={stage.stageLabel}
                  count={stage.count}
                  amountCents={summary.showFinancials ? stage.amountCents : null}
                  delayMs={idx * 50}
                />
              ))}
              {/* HoursTile relocated to MyHoursWidget at the top of the page
                  so the period total is visible without scrolling past the
                  pipeline tiles. The desktop layout has the widget there
                  too — the stage grid no longer includes it. */}
            </div>
          )}
        </section>
      )}

      {byDealQuery.data && byDealQuery.data.rows.length > 0 && (
        <section className="flex flex-col gap-3 pb-2">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm uppercase tracking-wide font-semibold text-brand-charcoal">
              Hours by deal
            </h2>
            <span className="text-xs text-brand-slate">
              {dateFmt.format(new Date(byDealQuery.data.startsAt))}–{dateFmt.format(new Date(byDealQuery.data.endsAt))}
            </span>
          </div>
          <div className="rounded-xl bg-brand-steel p-3 ring-1 ring-brand-steel">
            <div className="mx-auto h-64 w-full max-w-md">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="hours"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={1}
                  >
                    {pieData.map((_, idx) => (
                      <Cell key={idx} fill={SLICE_COLORS[idx % SLICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => {
                      const n = typeof value === 'number' ? value : Number(value);
                      return [Number.isFinite(n) ? formatHours(n * 60) : '0h', 'Hours'];
                    }}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 flex flex-col gap-1">
              {pieData.map((d, idx) => (
                <li key={d.name} className="flex items-center justify-between gap-2 text-xs">
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: SLICE_COLORS[idx % SLICE_COLORS.length] }}
                    />
                    <span className="truncate text-brand-charcoal">{d.name}</span>
                  </span>
                  <span className="shrink-0 tabular-nums text-brand-slate">{formatHours(d.minutes)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <TrendChart />

      {byDealQuery.data && byDealQuery.data.rows.length > 0 && byDealQuery.data.showFinancials && (
        <section className="flex flex-col gap-2 pb-6">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm uppercase tracking-wide font-semibold text-brand-charcoal">
              Budget vs billed
            </h2>
          </div>
          {byDealQuery.data.anyRateMissing && (
            <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
              Configure rates in <code>employees.hourly_rate_cents</code> to enable budget tracking.
              Money columns show <span className="font-semibold">—</span> until every contributing engineer has a rate.
            </p>
          )}
          <div className="flex flex-col divide-y divide-brand-steel rounded-xl bg-brand-steel ring-1 ring-brand-steel">
            {byDealQuery.data.rows.map((r) => {
              const consumedPct = r.billedCents !== null && r.amountCents !== null && r.amountCents > 0
                ? Math.min(100, Math.round((r.billedCents / r.amountCents) * 100))
                : null;
              const overBudget = r.moneyLeftCents !== null && r.moneyLeftCents < 0;
              return (
                <div key={r.dealId} className="flex flex-col gap-1 px-3 py-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-brand-charcoal">{dealDisplayName(r)}</span>
                    <span className="shrink-0 text-sm tabular-nums text-brand-charcoal">
                      {formatHours(r.minutes)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 text-xs text-brand-slate">
                    <span>
                      Budget:{' '}
                      <span className="tabular-nums text-brand-charcoal">
                        {r.amountCents === null ? '—' : cadFmt.format(r.amountCents / 100)}
                      </span>
                    </span>
                    <span>
                      Billed:{' '}
                      <span className="tabular-nums text-brand-charcoal">
                        {r.billedCents === null ? '—' : cadFmt.format(r.billedCents / 100)}
                      </span>
                    </span>
                    <span>
                      Left:{' '}
                      <span className={'tabular-nums ' + (overBudget ? 'text-red-700 font-semibold' : 'text-brand-charcoal')}>
                        {r.moneyLeftCents === null ? '—' : cadFmt.format(r.moneyLeftCents / 100)}
                      </span>
                    </span>
                    {consumedPct !== null && (
                      <span className="tabular-nums">{consumedPct}%</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}

interface StageTileProps {
  label: string;
  count: number;
  amountCents: number | null;
  delayMs: number;
}

function StageTile({ label, count, amountCents, delayMs }: StageTileProps): JSX.Element {
  const style = delayMs > 0 ? { animationDelay: `${delayMs}ms` } : undefined;
  return (
    <div
      className="stat-rise flex flex-col gap-1 rounded-xl bg-brand-steel p-4 ring-1 ring-brand-steel"
      style={style}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-brand-slate">
        {label}
      </div>
      <div className="text-3xl font-semibold tabular-nums text-brand-charcoal">
        {count}
      </div>
      {amountCents !== null && (
        <div className="text-xs tabular-nums text-brand-slate">
          {cadFmt.format(amountCents / 100)}
        </div>
      )}
    </div>
  );
}

interface HoursTileProps {
  minutes: number;
  delayMs: number;
}

function HoursTile({ minutes, delayMs }: HoursTileProps): JSX.Element {
  const style = delayMs > 0 ? { animationDelay: `${delayMs}ms` } : undefined;
  return (
    <div
      className="stat-rise flex flex-col gap-1 rounded-xl bg-brand-blue p-4 text-brand-white shadow-sm ring-1 ring-brand-blue"
      style={style}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-brand-white/80">
        My hours this period
      </div>
      <div className="text-3xl font-semibold tabular-nums">{formatHours(minutes)}</div>
      <div className="text-xs tabular-nums text-brand-white/70">across all deals</div>
    </div>
  );
}

function pluralizeBilling(slug: string, count: number): string {
  const label = isBillingType(slug) ? BILLING_LABELS_SHORT[slug].toLowerCase() : slug;
  return count === 1 ? label : `${label}s`;
}

interface MyHoursWidgetProps {
  isSupervisor: boolean;
}

// Top-of-page hours widget. Renders inline above SessionLog on mobile so the
// period total is visible the moment the page loads. Shares the React Query
// cache key with Dashboard's own summary query, so no extra round trip.
//
// Two display modes:
//   - Non-supervisor: counts of flat-billed work front and center
//     ("3 inspections · 2 drawings"); hourly time as a secondary line.
//     Hides the hourly-equivalent conversion entirely.
//   - Supervisor: total hours as the headline (existing UX); per-type
//     breakdown with the conversion ("3 inspections (6.0h)") below.
export function MyHoursWidget({ isSupervisor }: MyHoursWidgetProps): JSX.Element | null {
  const summaryQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => apiFetch<DashboardSummary>(`/dashboard`),
    refetchInterval: 30_000,
  });
  if (!summaryQuery.data) return null;
  const data = summaryQuery.data;
  const flatCounts = data.flatCountsThisPeriod;
  const hourlyMinutes = data.hourlyMinutesThisPeriod;
  const totalMinutes = data.minutesThisPeriod;
  const hasFlat = flatCounts.length > 0;
  const hasHourly = hourlyMinutes > 0;

  const headerText = !hasFlat
    ? 'My hours this period'
    : 'My work this period';

  return (
    <section className="flex flex-col gap-2">
      <div className="rounded-xl bg-brand-blue p-4 text-brand-white shadow-sm ring-1 ring-brand-blue">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-brand-white/80">
            {headerText}
          </span>
          <span className="text-xs text-brand-white/70">
            {dateFmt.format(new Date(data.payPeriodStartsAt))}–
            {dateFmt.format(new Date(data.payPeriodEndsAt))}
          </span>
        </div>

        {!hasFlat ? (
          // No flat work — legacy hours-only view.
          <>
            <div className="mt-1 text-3xl font-semibold tabular-nums">
              {formatHours(totalMinutes)}
            </div>
            <div className="text-xs tabular-nums text-brand-white/70">across all deals</div>
          </>
        ) : !isSupervisor ? (
          // Employee with flat work — counts are the primary unit.
          <>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {flatCounts.map((f) => (
                <span key={f.billingType} className="text-2xl font-semibold tabular-nums">
                  {f.count}{' '}
                  <span className="text-base font-medium text-brand-white/85">
                    {pluralizeBilling(f.billingType, f.count)}
                  </span>
                </span>
              ))}
            </div>
            {hasHourly && (
              <div className="mt-1 text-xs tabular-nums text-brand-white/80">
                + {formatHours(hourlyMinutes)} hourly across all deals
              </div>
            )}
            {!hasHourly && (
              <div className="text-xs tabular-nums text-brand-white/70">across all deals</div>
            )}
          </>
        ) : (
          // Supervisor with flat work — total hours headline, breakdown
          // below shows units AND the hourly conversion (Tim asked for both).
          <>
            <div className="mt-1 text-3xl font-semibold tabular-nums">
              {formatHours(totalMinutes)}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs tabular-nums text-brand-white/85">
              {flatCounts.map((f, idx) => (
                <span key={f.billingType}>
                  {idx > 0 && <span className="text-white/40">· </span>}
                  {f.count} {pluralizeBilling(f.billingType, f.count)}{' '}
                  <span className="text-white/65">({formatHours(f.minutes)} equiv)</span>
                </span>
              ))}
              {hasHourly && (
                <span>
                  <span className="text-white/40">· </span>
                  {formatHours(hourlyMinutes)} hourly
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
